import { Link } from '@tanstack/react-router';

import type { LinkedTaskSummaryItem } from '@/lib/api/generated/model';
import {
  CHILD_STATUS_DOT,
  CHILD_STATUS_LABEL,
  orderLinkedChildren,
} from '../linkedChildren';
import { StartChild } from './LinkedChildrenActions';
import { useSubtaskSelection } from '../hooks/useSubtaskSelection';
import { SplitSelectionControl } from './SplitSelectionControl';

/**
 * The created tasks, what needs someone first (spec 130 FR-008).
 * Spec 101 left them to the run page; the drawer listing them again
 * was asked for on 2026-10-05, so a failed child is named where the
 * parent is being read. Every child lives in the step's target
 * workflow, so that is the board each row opens on.
 *
 * With `selectable`, a row can be ticked and the ticked ones moved into a new
 * task (spec 137 US3). A tick whose child has since left the list (moved,
 * deleted) stops counting, so the button never offers a stale id (FR-020).
 */
export function LinkedChildrenList({
  workspaceId,
  parent,
  children,
  childWorkflowKey,
  selectable,
}: {
  workspaceId: string;
  parent: { id: string; title: string };
  children: LinkedTaskSummaryItem[];
  childWorkflowKey?: string;
  selectable: boolean;
}) {
  const { selected, toggle, clear } = useSubtaskSelection(
    children.map((child) => child.task_id),
  );

  if (children.length === 0) return null;

  return (
    <>
      <ul aria-label="Created tasks" className="mt-2 flex flex-col gap-1">
        {orderLinkedChildren(children).map((child) => (
          <li
            key={child.task_id}
            className="flex min-w-0 items-center gap-2 text-sm"
          >
            {selectable ? (
              <input
                type="checkbox"
                aria-label={`Select ${child.title}`}
                className="accent-primary size-4 shrink-0"
                checked={selected.has(child.task_id)}
                onChange={() => toggle(child.task_id)}
              />
            ) : null}
            <span
              aria-hidden="true"
              className={`size-2 shrink-0 rounded-full ${CHILD_STATUS_DOT[child.status]}`}
            />
            {childWorkflowKey ? (
              <Link
                to="/workspaces/$workspaceId/workflows/$workflowKey/board"
                params={{ workspaceId, workflowKey: childWorkflowKey }}
                search={{ task: child.task_id }}
                className="text-foreground min-w-0 flex-1 truncate underline-offset-4 hover:underline"
              >
                {child.title}
              </Link>
            ) : (
              <span className="text-foreground min-w-0 flex-1 truncate">
                {child.title}
              </span>
            )}
            <span className="text-muted-foreground shrink-0 text-xs">
              {CHILD_STATUS_LABEL[child.status]}
            </span>
            {child.status === 'not_started' ? (
              <StartChild
                workspaceId={workspaceId}
                childId={child.task_id}
                childTitle={child.title}
              />
            ) : null}
          </li>
        ))}
      </ul>

      {selectable ? (
        <SplitSelectionControl
          workspaceId={workspaceId}
          parent={parent}
          selectedIds={[...selected]}
          total={children.length}
          onSplit={clear}
        />
      ) : null}
    </>
  );
}
